import { useCallback, useEffect, useRef } from "react";
import { create } from "zustand";

import { initializeUserLanguage } from "@/utils/i18n/initializeLanguage";

import { UserInfo } from "../rest/models/account";
import { fetchUserInfo } from "../rest/services/accountService";

/**
 * loading: nothing loaded yet, or a request is running
 * ready:   user is set
 * error:   the last load failed; call retry(). Without it pages spin forever.
 */
export type UserLoadStatus = "loading" | "ready" | "error";

interface UserState {
  user: UserInfo | null;
  status: UserLoadStatus;
  // A request is in flight; separate from status so concurrent callers don't fetch twice.
  loading: boolean;
}

interface UserActions {
  load: () => Promise<void>;
  retry: () => Promise<void>;
  clear: () => void;
}

export const useUserGlobal = create<UserState & UserActions>((set, get) => {
  return {
    user: null,
    status: "loading",
    loading: false,
    load: async () => {
      const state = useUserGlobal.getState();

      if (state.loading || state.user) {
        return;
      }

      set({ loading: true, status: "loading" });
      const account = await fetchUserInfo().catch(() => null);
      if (!account?.success) {
        set({ loading: false, status: "error" });
        return;
      }

      set((state) => ({
        // Keep the same object for the same user so subscribers don't re-render.
        user: state.user?.userId === account.result.userId ? state.user : account.result,
        loading: false,
        status: "ready",
      }));
      await initializeUserLanguage(account.result);
    },
    retry: () => get().load(),
    clear: () => {
      set({ user: null, loading: false, status: "loading" });
    },
  };
});

export const useUser = () => {
  const loadingRef = useRef(false);
  const user = useUserGlobal(useCallback((state) => state.user, []));
  const load = useUserGlobal(useCallback((state) => state.load, []));

  // Use useEffect with proper dependencies
  useEffect(() => {
    if ((user === null || user === undefined) && !loadingRef.current) {
      loadingRef.current = true;
      const loadData = async () => {
        await load();
        loadingRef.current = false;
      };
      loadData();
    }
  }, [user, load]);

  return user;
};

export const useUserStatus = () => {
  const status = useUserGlobal(useCallback((state) => state.status, []));
  const retry = useUserGlobal(useCallback((state) => state.retry, []));
  return { status, retry };
};
